import * as THREE from 'three';

import { CHURCH_CONFIG, TELEPORT_CONFIG } from '../config.js';

const DEFAULT_MARKER = Object.freeze({
  radius: 0.22,
  validColor: 0x55e58b,
  invalidColor: 0xff5a5f,
  opacity: 0.9,
  surfaceOffset: 0.018,
});

function readX(value) {
  return Array.isArray(value) ? (value[0] ?? 0) : (value?.x ?? 0);
}

function readZ(value) {
  if (Array.isArray(value)) return value.length > 2 ? (value[2] ?? 0) : (value[1] ?? 0);
  return value?.z ?? value?.y ?? 0;
}

/** Raycast-based teleportation that can later be fed a dedicated navmesh. */
export class Teleportation extends THREE.EventDispatcher {
  constructor({
    playerRig,
    camera = null,
    renderer = null,
    controllers,
    teleportableMeshes = [],
    markerParent = null,
    validator = null,
    bounds = undefined,
    blockedZones = null,
    config = TELEPORT_CONFIG,
    bindSelect = true,
    activationEvent = 'selectstart',
    activationGuard = null,
    syncRayLength = true,
    onTeleport = null,
  } = {}) {
    super();

    if (!playerRig?.isObject3D) throw new TypeError('Teleportation requiere playerRig.');
    if (!controllers?.states) throw new TypeError('Teleportation requiere una instancia de Controllers.');

    this.playerRig = playerRig;
    this.camera = camera;
    this.renderer = renderer;
    this.controllers = controllers;
    this.teleportableMeshes = teleportableMeshes;
    this.markerParent = markerParent ?? playerRig.parent ?? playerRig;
    this.validator = validator;
    this.blockedZones = blockedZones ?? config?.blockedZones ?? [];
    this.bounds = bounds ?? config?.bounds ?? null;
    this.visitRadius = config?.visitRadius ?? null;
    const visitCenter = config?.visitCenter ?? CHURCH_CONFIG?.position;
    this.visitCenterX = visitCenter?.[0] ?? visitCenter?.x ?? 0;
    this.visitCenterZ = visitCenter?.[2] ?? visitCenter?.z ?? 0;
    this.maxDistance = config?.maxDistance ?? 18;
    this.minSurfaceNormalY = config?.minSurfaceNormalY
      ?? Math.cos(THREE.MathUtils.degToRad(config?.maxSlopeDegrees ?? 52));
    this.enabled = true;
    this.onTeleport = onTeleport;
    this.activationEvent = activationEvent;
    this.activationGuard = activationGuard;
    this.syncRayLength = syncRayLength;

    this.markerConfig = {
      ...DEFAULT_MARKER,
      radius: config?.markerRadius ?? DEFAULT_MARKER.radius,
      validColor: config?.markerColorValid ?? DEFAULT_MARKER.validColor,
      invalidColor: config?.markerColorInvalid ?? DEFAULT_MARKER.invalidColor,
      ...(config?.marker ?? {}),
    };
    this.raycaster = new THREE.Raycaster();
    this.raycaster.near = 0.04;
    this.raycaster.far = this.maxDistance;

    this._rayOrigin = new THREE.Vector3();
    this._rayDirection = new THREE.Vector3();
    this._surfaceNormal = new THREE.Vector3();
    this._localSurfaceNormal = new THREE.Vector3();
    this._markerNormalAxis = new THREE.Vector3(0, 0, 1);
    this._normalMatrix = new THREE.Matrix3();
    this._rigWorldPosition = new THREE.Vector3();
    this._headWorldPosition = new THREE.Vector3();
    this._worldOffset = new THREE.Vector3();
    this._parentQuaternion = new THREE.Quaternion();
    this._destination = new THREE.Vector3();
    this._intersections = [];
    this._selectBindings = [];
    this._validatorErrorReported = false;

    this._createMarkerResources();
    this.aimStates = [];

    for (let i = 0; i < controllers.states.length; i += 1) {
      const state = controllers.states[i];
      const marker = this._createMarker(i);
      this.aimStates.push({
        index: i,
        controllerState: state,
        marker,
        point: new THREE.Vector3(),
        normal: new THREE.Vector3(0, 1, 0),
        object: null,
        distance: Infinity,
        hasHit: false,
        valid: false,
        intersection: null,
        originalRayLength: state.ray.scale.z,
      });

      if (syncRayLength) state.ray.scale.z = this.maxDistance;

      if (bindSelect) {
        const handler = () => this.tryTeleport(i);
        state.controller.addEventListener(activationEvent, handler);
        this._selectBindings.push({ controller: state.controller, handler });
      }
    }
  }

  _createMarkerResources() {
    const { radius, validColor, invalidColor, opacity } = this.markerConfig;
    this._ringGeometry = new THREE.RingGeometry(radius * 0.62, radius, 32);
    this._crossGeometry = new THREE.BufferGeometry();
    this._crossGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
      -radius * 0.52, -radius * 0.52, 0,
      radius * 0.52, radius * 0.52, 0,
      -radius * 0.52, radius * 0.52, 0,
      radius * 0.52, -radius * 0.52, 0,
    ], 3));

    const materialOptions = {
      transparent: true,
      opacity,
      depthWrite: false,
      toneMapped: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
    };
    this._validMaterial = new THREE.MeshBasicMaterial({
      ...materialOptions,
      color: validColor,
    });
    this._invalidMaterial = new THREE.MeshBasicMaterial({
      ...materialOptions,
      color: invalidColor,
    });
    this._crossMaterial = new THREE.LineBasicMaterial({
      color: invalidColor,
      transparent: true,
      opacity,
      depthWrite: false,
      toneMapped: false,
    });
  }

  _createMarker(index) {
    const marker = new THREE.Group();
    const ring = new THREE.Mesh(this._ringGeometry, this._validMaterial);
    const cross = new THREE.LineSegments(this._crossGeometry, this._crossMaterial);

    marker.name = `TeleportMarker-${index}`;
    marker.visible = false;
    marker.renderOrder = 8;
    ring.frustumCulled = false;
    cross.frustumCulled = false;
    cross.position.z = 0.002;
    cross.visible = false;
    marker.userData.ring = ring;
    marker.userData.cross = cross;
    marker.add(ring, cross);
    this.markerParent.add(marker);
    return marker;
  }

  setTeleportableMeshes(meshes) {
    this.teleportableMeshes = meshes ?? [];
    return this;
  }

  addTeleportableMesh(mesh) {
    if (mesh && !this.teleportableMeshes.includes(mesh)) this.teleportableMeshes.push(mesh);
    return this;
  }

  removeTeleportableMesh(mesh) {
    const index = this.teleportableMeshes.indexOf(mesh);
    if (index >= 0) this.teleportableMeshes.splice(index, 1);
    return this;
  }

  setValidator(validator) {
    this.validator = validator;
    this._validatorErrorReported = false;
    return this;
  }

  setBlockedZones(zones) {
    this.blockedZones = zones ?? [];
    return this;
  }

  setBounds(bounds) {
    this.bounds = bounds;
    return this;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) this.clearAims();
    return this;
  }

  getAimState(handOrIndex) {
    const controllerState = this.controllers.getState(handOrIndex);
    return controllerState ? this.aimStates[controllerState.index] : null;
  }

  update() {
    if (!this.enabled || this.teleportableMeshes.length === 0) {
      this.clearAims();
      return this.aimStates;
    }

    for (let i = 0; i < this.aimStates.length; i += 1) {
      const aim = this.aimStates[i];
      const state = aim.controllerState;

      if (!state.connected || !state.rayEnabled) {
        this._clearAim(aim);
        continue;
      }

      state.controller.updateWorldMatrix(true, false);
      this._rayOrigin.setFromMatrixPosition(state.controller.matrixWorld);
      this._rayDirection.set(0, 0, -1).transformDirection(state.controller.matrixWorld);
      this.raycaster.set(this._rayOrigin, this._rayDirection);

      this._intersections.length = 0;
      this.raycaster.intersectObjects(this.teleportableMeshes, true, this._intersections);
      const hit = this._intersections[0];

      if (!hit) {
        this._clearAim(aim);
        continue;
      }

      aim.point.copy(hit.point);
      aim.object = hit.object;
      aim.distance = hit.distance;
      aim.hasHit = true;
      aim.intersection = hit;
      aim.valid = this._isIntersectionWalkable(hit, aim.normal);
      this._showMarker(aim);
    }

    return this.aimStates;
  }

  isPointAllowed(point, intersection = null) {
    if (!this._isInsideBounds(point) || this._isBlocked(point)) return false;

    if (this.validator) {
      try {
        return this.validator(point, intersection, this) !== false;
      } catch (error) {
        if (!this._validatorErrorReported) {
          console.error('[Teleportation] El validador de destino ha fallado.', error);
          this._validatorErrorReported = true;
        }
        return false;
      }
    }

    return true;
  }

  _isIntersectionWalkable(hit, targetNormal) {
    if (hit.distance > this.maxDistance) return false;

    targetNormal.set(0, 1, 0);
    if (hit.face) {
      this._normalMatrix.getNormalMatrix(hit.object.matrixWorld);
      this._surfaceNormal.copy(hit.face.normal).applyNormalMatrix(this._normalMatrix);
      if (this._surfaceNormal.y < this.minSurfaceNormalY) return false;
      targetNormal.copy(this._surfaceNormal);
    }

    return this.isPointAllowed(hit.point, hit);
  }

  _isInsideBounds(point) {
    const bounds = this.bounds;
    if (!bounds) {
      if (this.visitRadius === null) return true;
      const dx = point.x - this.visitCenterX;
      const dz = point.z - this.visitCenterZ;
      return (dx * dx) + (dz * dz) <= this.visitRadius * this.visitRadius;
    }

    if (Array.isArray(bounds)) {
      return point.x >= bounds[0]
        && point.x <= bounds[1]
        && point.z >= bounds[2]
        && point.z <= bounds[3];
    }

    if (bounds.type === 'circle' || bounds.radius !== undefined) {
      const centerX = bounds.centerX ?? readX(bounds.center);
      const centerZ = bounds.centerZ ?? readZ(bounds.center);
      const dx = point.x - centerX;
      const dz = point.z - centerZ;
      return (dx * dx) + (dz * dz) <= bounds.radius * bounds.radius;
    }

    const minX = bounds.minX ?? (bounds.min ? readX(bounds.min) : -Infinity);
    const maxX = bounds.maxX ?? (bounds.max ? readX(bounds.max) : Infinity);
    const minZ = bounds.minZ ?? (bounds.min ? readZ(bounds.min) : -Infinity);
    const maxZ = bounds.maxZ ?? (bounds.max ? readZ(bounds.max) : Infinity);
    return point.x >= minX && point.x <= maxX && point.z >= minZ && point.z <= maxZ;
  }

  _isBlocked(point) {
    for (let i = 0; i < this.blockedZones.length; i += 1) {
      const zone = this.blockedZones[i];
      if (!zone) continue;
      if (typeof zone.containsPoint === 'function' && zone.containsPoint(point)) return true;

      const center = zone.center ?? zone.position;
      if (zone.radius !== undefined && center) {
        const dx = point.x - readX(center);
        const dz = point.z - readZ(center);
        if ((dx * dx) + (dz * dz) <= zone.radius * zone.radius) return true;
        continue;
      }

      let minX = zone.minX;
      let maxX = zone.maxX;
      let minZ = zone.minZ;
      let maxZ = zone.maxZ;

      if (zone.min && zone.max) {
        minX ??= readX(zone.min);
        maxX ??= readX(zone.max);
        minZ ??= readZ(zone.min);
        maxZ ??= readZ(zone.max);
      } else if (center && zone.size) {
        const halfX = readX(zone.size) * 0.5;
        const halfZ = readZ(zone.size) * 0.5;
        minX ??= readX(center) - halfX;
        maxX ??= readX(center) + halfX;
        minZ ??= readZ(center) - halfZ;
        maxZ ??= readZ(center) + halfZ;
      }

      if (minX !== undefined && maxX !== undefined
        && minZ !== undefined && maxZ !== undefined
        && point.x >= minX && point.x <= maxX
        && point.z >= minZ && point.z <= maxZ) return true;
    }

    return false;
  }

  _showMarker(aim) {
    const marker = aim.marker;
    marker.visible = true;
    marker.userData.ring.material = aim.valid ? this._validMaterial : this._invalidMaterial;
    marker.userData.cross.visible = !aim.valid;

    marker.position.copy(aim.point).addScaledVector(aim.normal, this.markerConfig.surfaceOffset);
    this.markerParent.updateWorldMatrix(true, false);
    this.markerParent.worldToLocal(marker.position);

    this._localSurfaceNormal.copy(aim.normal);
    this.markerParent.getWorldQuaternion(this._parentQuaternion).invert();
    this._localSurfaceNormal.applyQuaternion(this._parentQuaternion).normalize();
    marker.quaternion.setFromUnitVectors(this._markerNormalAxis, this._localSurfaceNormal);
  }

  _clearAim(aim) {
    aim.hasHit = false;
    aim.valid = false;
    aim.object = null;
    aim.distance = Infinity;
    aim.intersection = null;
    aim.marker.visible = false;
  }

  clearAims() {
    for (let i = 0; i < this.aimStates.length; i += 1) {
      this._clearAim(this.aimStates[i]);
    }
  }

  tryTeleport(handOrIndex) {
    if (!this.enabled) return false;
    const aim = this.getAimState(handOrIndex);
    if (!aim?.valid) return false;
    if (this.activationGuard
      && this.activationGuard(aim.controllerState, aim, this) === false) return false;

    this._destination.copy(aim.point);
    this.playerRig.updateWorldMatrix(true, false);
    this.playerRig.getWorldPosition(this._rigWorldPosition);

    if (this.camera) {
      // Three.js copies the XR pose into the user camera during render. Since
      // that camera is actually parented to the rig, its world transform also
      // includes teleport/snap-turn offsets; the internal ArrayCamera does not
      // have that parent and must not be queried with getWorldPosition().
      this.camera.getWorldPosition(this._headWorldPosition);
      this._worldOffset.set(
        this._destination.x - this._headWorldPosition.x,
        this._destination.y - this._rigWorldPosition.y,
        this._destination.z - this._headWorldPosition.z,
      );
    } else {
      this._worldOffset.subVectors(this._destination, this._rigWorldPosition);
    }

    if (this.playerRig.parent) {
      this.playerRig.parent.getWorldQuaternion(this._parentQuaternion).invert();
      this._worldOffset.applyQuaternion(this._parentQuaternion);
    }

    this.playerRig.position.add(this._worldOffset);
    this.playerRig.updateMatrixWorld(true);
    this.clearAims();

    this.onTeleport?.(this._destination, aim.controllerState, this);
    this.dispatchEvent({
      type: 'teleport',
      point: this._destination,
      controllerState: aim.controllerState,
    });
    return true;
  }

  dispose() {
    for (let i = 0; i < this._selectBindings.length; i += 1) {
      const binding = this._selectBindings[i];
      binding.controller.removeEventListener(this.activationEvent, binding.handler);
    }
    this._selectBindings.length = 0;

    for (let i = 0; i < this.aimStates.length; i += 1) {
      const aim = this.aimStates[i];
      aim.marker.removeFromParent();
      if (this.syncRayLength) aim.controllerState.ray.scale.z = aim.originalRayLength;
    }
    this.aimStates.length = 0;

    this._ringGeometry.dispose();
    this._crossGeometry.dispose();
    this._validMaterial.dispose();
    this._invalidMaterial.dispose();
    this._crossMaterial.dispose();
  }
}

export default Teleportation;
